import { useCallback, useMemo } from 'react';

import { useAtomCallbackMemoized } from '../../../hooks/useAtomCallbackMemoized';
import { useVideoPlatformCallback } from '../../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../../segment/segment';
import { EventTypeEnum } from '../../../segment/types';
import { sessionInfoAtom } from '../../../state';
import { UserType } from '../../../types';
import { entityIdToPatientShortId } from '../../../video-sdk/core/room/participant/utils';

function getShortIdFieldFromUserType(
  userType: UserType,
  entityId: string,
):
  | {
      patientShortId: string;
    }
  | {
      providerShortId: string;
    } {
  switch (userType) {
    case UserType.CLIENT:
      return { patientShortId: entityIdToPatientShortId(entityId) };
    case UserType.PROVIDER:
    default:
      return { providerShortId: entityId };
  }
}

export function useRemoteParticipantReconnectingTrackingEvents() {
  const { userViewed } = useTrackEvent();
  const getAppointmentShortId = useAtomCallbackMemoized((get) => get(sessionInfoAtom)?.appointment?.shortId, []);
  const getVideoPlatform = useVideoPlatformCallback();

  const getEventParams = useCallback(
    (...args: Parameters<typeof getShortIdFieldFromUserType>) => ({
      appointmentShortId: getAppointmentShortId(),
      source: getVideoPlatform() ?? '',
      ...getShortIdFieldFromUserType(...args),
    }),
    [getAppointmentShortId, getVideoPlatform],
  );

  return useMemo(() => {
    return {
      trackReconnectingRemoteParticipantViewed(...args: Parameters<typeof getShortIdFieldFromUserType>) {
        userViewed(EventTypeEnum.RECONNECTING_REMOTE_PARTICIPANT_VIEWED, getEventParams(...args));
      },
      trackReconnectedRemoteParticipantViewed(...args: Parameters<typeof getShortIdFieldFromUserType>) {
        userViewed(EventTypeEnum.RECONNECTED_REMOTE_PARTICIPANT_VIEWED, getEventParams(...args));
      },
    };
  }, [getEventParams, userViewed]);
}
